import React, { useState, useEffect, useMemo } from 'react';
import { 
  ArrowLeftIcon, 
  DocumentIcon,
  EyeIcon, 
  ArrowDownTrayIcon, 
  MagnifyingGlassIcon,
  FunnelIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';
import { DocumentNode } from '../../types/documents';
import { documentService } from '../../services/documentService';
import { useToast } from '../UI/ToastProvider';
import documentDownloadService from '../../services/documentDownloadService';
import { getFileTypeIcon } from './fileTypeIcons';
import { useDocumentPermissions } from '../../hooks/useDocumentPermissions';
import { ActionBlockedModal } from '../UI/ActionBlockedModal';

interface DocumentListByFilterProps {
  filterType: 'category' | 'tag';
  filterId: string;
  filterName: string;
  onBack: () => void;
  onNodeSelect: (node: DocumentNode) => void;
  onRefresh?: () => void;
}

export const DocumentListByFilter: React.FC<DocumentListByFilterProps> = ({
  filterType,
  filterId,
  filterName,
  onBack,
  onNodeSelect,
  onRefresh
}) => {
  const [documents, setDocuments] = useState<DocumentNode[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const { addToast } = useToast();

  // Estados para modal de ação bloqueada
  const [showActionBlockedModal, setShowActionBlockedModal] = useState(false);
  const [blockedActionName, setBlockedActionName] = useState<string>('');

  // Hook para gerenciar permissões (usando o primeiro documento como referência)
  const { permissions } = useDocumentPermissions(documents.length > 0 ? documents[0].id : null);

  // Função para verificar se o documento é eletrônico (edoc)
  const isElectronicDocument = (node: DocumentNode): boolean => {
    return node.nodeType === 'edoc';
  };

  useEffect(() => {
    loadDocuments();
  }, [filterType, filterId]);

  const loadDocuments = async () => {
    try {
      setIsLoading(true);
      let data: DocumentNode[] = [];
      
      if (filterType === 'category') {
        // Buscar documentos por categoria usando a API correta
        const apiUrl = process.env.REACT_APP_API_URL || 'https://localhost:7180';
        const response = await fetch(`${apiUrl}/documents/categories/${filterId}`, {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${sessionStorage.getItem('@KnowInDoc:accessToken')}`,
            'tenant_id': sessionStorage.getItem('@KnowInDoc:customerToken') || '',
            'customer_id': sessionStorage.getItem('@KnowInDoc:customer_id') || '',
            'user_id': sessionStorage.getItem('@KnowInDoc:user_id') || ''
          }
        });
        if (!response.ok) {
          throw new Error(`Erro ${response.status}: ${response.statusText}`);
        }
        const documentsData = await response.json();
        
        data = documentsData.map((doc: any) => ({
          id: doc.id?.toString() || '',
          customerId: doc.customerId?.toString() || '',
          title: doc.name?.toString() || doc.title?.toString() || 'Documento sem título',
          description: doc.description?.toString() || '',
          nodeType: 'document',
          parentDocumentId: doc.parentDocumentId?.toString() || null,
          level: 0,
          order: 0,
          children: [],
          countChild: 0,
          path: doc.path?.toString() || '/',
          fullPath: doc.fullPath?.toString() || '/',
          status: doc.status?.toString() || 'active',
          currentVersion: doc.currentVersion,
          content: doc.content,
          categories: Array.isArray(doc.categories) ? doc.categories : [],
          tags: Array.isArray(doc.tags) ? doc.tags.filter((tag: any) => typeof tag === 'string') : [],
          metadata: {
            resume: doc.resume?.toString() || '',
            fileName: doc.fileName?.toString() || doc.name?.toString() || '',
            contentType: doc.contentType?.toString() || 'application/octet-stream',
            size: doc.size?.toString() || '0',
            categories: Array.isArray(doc.categories) ? doc.categories.filter((cat: any) => typeof cat === 'string') : [],
            tags: Array.isArray(doc.tags) ? doc.tags.filter((tag: any) => typeof tag === 'string') : [],
            author: doc.author?.toString() || doc.createdBy?.toString() || 'Sistema',
            createdAt: doc.createdAt?.toString() || new Date().toISOString(),
            updater: doc.updater?.toString() || doc.lastModifiedBy?.toString() || 'Sistema',
            updatedAt: doc.updatedAt?.toString() || new Date().toISOString(),
            // Campos legados para compatibilidade
            content: doc.content?.toString() || '',
            category: doc.category?.toString() || '',
            type: doc.type?.toString() || 'document',
            url: doc.url?.toString() || '',
            createdBy: doc.createdBy?.toString() || 'Sistema',
            lastModifiedBy: doc.lastModifiedBy?.toString() || 'Sistema',
            lastModifiedAt: doc.updatedAt?.toString() || new Date().toISOString(),
          },
        }));
      } else if (filterType === 'tag') {
        // Buscar documentos por tag usando a API correta
        const apiUrl = process.env.REACT_APP_API_URL || 'https://localhost:7180';
        const response = await fetch(`${apiUrl}/documents/tags/${filterId}`, {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${sessionStorage.getItem('@KnowInDoc:accessToken')}`,
            'tenant_id': sessionStorage.getItem('@KnowInDoc:customerToken') || '',
            'customer_id': sessionStorage.getItem('@KnowInDoc:customer_id') || '',
            'user_id': sessionStorage.getItem('@KnowInDoc:user_id') || ''
          }
        });
        if (!response.ok) {
          throw new Error(`Erro ${response.status}: ${response.statusText}`);
        }
        const documentsData = await response.json();
        
        data = documentsData.map((doc: any) => ({
          id: doc.id?.toString() || '',
          customerId: doc.customerId?.toString() || '',
          title: doc.name?.toString() || doc.title?.toString() || 'Documento sem título',
          description: doc.description?.toString() || '',
          nodeType: 'document',
          parentDocumentId: doc.parentDocumentId?.toString() || null,
          level: 0,
          order: 0,
          children: [],
          countChild: 0,
          path: doc.path?.toString() || '/',
          fullPath: doc.fullPath?.toString() || '/',
          status: doc.status?.toString() || 'active',
          currentVersion: doc.currentVersion,
          content: doc.content,
          categories: Array.isArray(doc.categories) ? doc.categories : [],
          tags: Array.isArray(doc.tags) ? doc.tags.filter((tag: any) => typeof tag === 'string') : [],
          metadata: {
            resume: doc.resume?.toString() || '',
            fileName: doc.fileName?.toString() || doc.name?.toString() || '',
            contentType: doc.contentType?.toString() || 'application/octet-stream',
            size: doc.size?.toString() || '0',
            categories: Array.isArray(doc.categories) ? doc.categories.filter((cat: any) => typeof cat === 'string') : [],
            tags: Array.isArray(doc.tags) ? doc.tags.filter((tag: any) => typeof tag === 'string') : [],
            author: doc.author?.toString() || doc.createdBy?.toString() || 'Sistema',
            createdAt: doc.createdAt?.toString() || new Date().toISOString(),
            updater: doc.updater?.toString() || doc.lastModifiedBy?.toString() || 'Sistema',
            updatedAt: doc.updatedAt?.toString() || new Date().toISOString(),
            // Campos legados para compatibilidade
            content: doc.content?.toString() || '',
            category: doc.category?.toString() || '',
            type: doc.type?.toString() || 'document',
            url: doc.url?.toString() || '',
            createdBy: doc.createdBy?.toString() || 'Sistema',
            lastModifiedBy: doc.lastModifiedBy?.toString() || 'Sistema',
            lastModifiedAt: doc.updatedAt?.toString() || new Date().toISOString(),
          },
        }));
      }
      
      setDocuments(data);
    } catch (error) {
      // Tratamento específico de erro para evitar mensagens duplicadas
      const errorMessage = error instanceof Error ? error.message : 'Erro desconhecido';
      console.error(`Erro ao carregar documentos por ${filterType === 'category' ? 'categoria' : 'tag'}:`, errorMessage);
      
      // Só exibir toast se não for um erro de rede ou autenticação
      if (!errorMessage.includes('Failed to fetch') && !errorMessage.includes('401')) {
        addToast(`Erro ao carregar documentos: ${errorMessage}`, 'error');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const filteredDocuments = documents.filter(doc => {
    const matchesSearch = doc.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (doc.description && doc.description.toLowerCase().includes(searchTerm.toLowerCase()));
    
    return matchesSearch;
  });

  const handleDownload = async (doc: DocumentNode) => {
    try {
      if (!doc.id || doc.id === 'root') {
        addToast('ID do documento inválido', 'error');
        return;
      }

      await documentDownloadService.downloadAndSaveDocument(
        doc.id,
        doc.metadata?.fileName || doc.title
      );

      addToast('Documento baixado com sucesso!', 'success');
    } catch (error) {
      console.error('Erro ao baixar documento:', error);
      
      let errorMessage = 'Erro ao baixar documento';
      if (error instanceof Error) {
        if (error.message.includes('401')) {
          errorMessage = 'Não autorizado. Faça login novamente.';
        } else if (error.message.includes('404')) {
          errorMessage = 'Documento não encontrado.';
        } else if (error.message.includes('403')) {
          errorMessage = 'Acesso negado a este documento.';
        } else {
          errorMessage = error.message;
        }
      }
      
      addToast(errorMessage, 'error');
    }
  };

  // Funções para mostrar modal de ação bloqueada
  const showActionBlocked = (actionName: string) => {
    setBlockedActionName(actionName);
    setShowActionBlockedModal(true);
  };

  const handleViewDocument = (doc: DocumentNode) => {
    showActionBlocked('Visualizar');
  };

  const getFileExtension = (doc: DocumentNode): string => {
    const fileName = doc.metadata?.fileName || doc.title || '';
    return fileName.split('.').pop()?.toLowerCase() || '';
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={onBack}
            className="inline-flex items-center text-gray-600 hover:text-gray-900 transition-colors"
          >
            <ArrowLeftIcon className="h-5 w-5 mr-2" />
            Voltar
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              Documentos por {filterType === 'category' ? 'Categoria' : 'Tag'}
            </h1>
            <p className="text-gray-600">
              {filterType === 'category' ? 'Categoria' : 'Tag'}: <span className="font-medium">{filterName}</span>
            </p>
          </div>
        </div>
        <div className="text-sm text-gray-500">
          {filteredDocuments.length} documento{filteredDocuments.length !== 1 ? 's' : ''} encontrado{filteredDocuments.length !== 1 ? 's' : ''}
        </div>
      </div>

      {/* Search */}
      <div className="max-w-md">
        <div className="relative">
          <input
            type="text"
            placeholder="Buscar documentos..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
          <MagnifyingGlassIcon className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
        </div>
      </div>

      {/* Documents List */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        {filteredDocuments.length > 0 ? (
          <ul className="divide-y divide-gray-200">
            {filteredDocuments.map((doc) => (
              <li key={doc.id} className="hover:bg-gray-50">
                <div className="px-6 py-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                      <div className="flex-shrink-0">
                        <span className="text-2xl">{getFileTypeIcon(getFileExtension(doc))}</span>
                      </div>
                      
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center space-x-2">
                          <p className="text-sm font-medium text-gray-900 truncate">
                            {typeof doc.title === 'string' ? doc.title : 'Documento sem título'}
                          </p>
                          {doc.metadata?.tags && Array.isArray(doc.metadata.tags) && doc.metadata.tags.length > 0 && (
                            <div className="flex space-x-1">
                              {doc.metadata.tags
                                .filter(tag => typeof tag === 'string')
                                .slice(0, 3)
                                .map((tag, index) => (
                                  <span
                                    key={index}
                                    className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                                  >
                                    #{tag}
                                  </span>
                                ))}
                              {doc.metadata.tags.filter(tag => typeof tag === 'string').length > 3 && (
                                <span className="text-xs text-gray-500">
                                  +{doc.metadata.tags.filter(tag => typeof tag === 'string').length - 3}
                                </span>
                              )}
                            </div>
                          )}
                        </div>
                        
                        <div className="mt-1 flex items-center space-x-4 text-sm text-gray-500">
                          <span>
                            Criado por: {doc.metadata?.author || doc.metadata?.createdBy || 'Sistema'}
                          </span>
                          <span>
                            Última modificação: {doc.metadata?.updatedAt || doc.metadata?.lastModifiedAt || ''}
                          </span>
                          {doc.metadata?.size && typeof doc.metadata.size === 'string' && doc.nodeType !== 'folder' && (
                            <span>{doc.metadata.size}</span>
                          )}
                        </div>
                        
                        {/* Caminho do documento */}
                        {doc.path && doc.path !== '/' && (
                          <div className="mt-1 text-sm text-gray-500">
                            <span className="font-medium">Caminho:</span> {doc.path.length > 50 ? 
                              `${doc.path.substring(0, 47)}...` : doc.path
                            }
                          </div>
                        )}
                      </div>
                    </div>
                    
                    <div className="flex items-center space-x-2">
                      {/* Só mostra o título se houver pelo menos uma ação visível */}
                      {(isElectronicDocument(doc) && permissions.canView) ||
                       (!isElectronicDocument(doc) && permissions.canView) ? (
                        <div className="flex items-center space-x-2">
                          {/* Botão de Visualizar - apenas para documentos eletrônicos e se tiver permissão */}
                          {isElectronicDocument(doc) && permissions.canView && (
                            <button
                              onClick={() => onNodeSelect(doc)}
                              className="p-2 text-gray-400 hover:text-blue-600 transition-colors"
                              title="Visualizar"
                            >
                              <EyeIcon className="h-4 w-4" />
                            </button>
                          )}
                          
                          {/* Botão de Baixar - apenas para documentos que não são eletrônicos e se tiver permissão de visualização */}
                          {!isElectronicDocument(doc) && permissions.canView && (
                            <button
                              onClick={() => handleDownload(doc)}
                              className="p-2 text-gray-400 hover:text-blue-600 transition-colors"
                              title="Baixar"
                            >
                              <ArrowDownTrayIcon className="h-4 w-4" />
                            </button>
                          )}
                        </div>
                      ) : (
                        /* Botões desabilitados com modal de ação bloqueada */
                        <div className="flex items-center space-x-2">
                          {isElectronicDocument(doc) && (
                            <button
                              onClick={() => handleViewDocument(doc)}
                              className="p-2 text-gray-300 cursor-not-allowed"
                              title="Visualizar (Bloqueado)"
                            >
                              <EyeIcon className="h-4 w-4" />
                            </button>
                          )}
                          
                          {!isElectronicDocument(doc) && (
                            <button
                              onClick={() => showActionBlocked('Baixar')}
                              className="p-2 text-gray-300 cursor-not-allowed"
                              title="Baixar (Bloqueado)"
                            >
                              <ArrowDownTrayIcon className="h-4 w-4" />
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        ) : (
          <div className="text-center py-12">
            <DocumentIcon className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">
              Nenhum documento encontrado
            </h3>
            <p className="mt-1 text-sm text-gray-500">
              {searchTerm ? 'Tente ajustar os termos de busca.' : 'Não há documentos nesta categoria/tag.'}
            </p>
          </div>
        )}
      </div>

      {/* Modal de ação bloqueada */}
      <ActionBlockedModal
        isOpen={showActionBlockedModal}
        onClose={() => setShowActionBlockedModal(false)}
        actionName={blockedActionName}
      />
    </div>
  );
}; 